import {
  distinctUntilChanged,
  filter,
  switchMap,
  take,
  takeUntil,
} from 'rxjs/operators';
import { Subject } from 'rxjs';
import { VericureUser } from '../models/vericure-user';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UserAuthService } from '../UserAuth.service';
import { GoogleIdentityService } from '../auth/google-identity.service';
import { ExternalIdSignInService } from '../auth/external-id-sign-in.service';
import {
  DiscoverError,
  DiscoverFailure,
  HomeRealmService,
  SignInRoute,
  domainOf,
  forgetRememberedEmail,
  isPlausibleEmail,
  readRememberedEmail,
  rememberEmail,
} from '../auth/home-realm.service';
import { Router } from '@angular/router';
import { ProjectService } from '../services/project.service';
import { TranslationService } from '../i18n/translation.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('googleButtonHost') googleButtonHost?: ElementRef<HTMLElement>;

  /** True only when a Google client id is configured for this environment. */
  get googleSignInAvailable(): boolean {
    return this.googleIdentity.isConfigured;
  }

  /**
   * Set when a Google sign-in SUCCEEDED but the account is not onboarded to a Vericure company.
   *
   * Deliberately distinct from /unauthorized. For Google this is an ONBOARDING gap, not a denial:
   * the person proved who they are, and nobody has created their user document yet. Routing them to
   * "access denied" would send both them and whoever they ask down the wrong path.
   */
  googleNotOnboarded = false;
  /** Torn down with the component, so no subscription outlives it. */
  private readonly destroy$ = new Subject<void>();

  /** True when the authorization check could not reach a verdict. */
  authCheckFailed = false;

  authStatusChceked = false;
  isAuthenticated = false;

  constructor(
    private authService: UserAuthService,
    private projectService: ProjectService,
    private _router: Router,
    private translation: TranslationService,
    private googleIdentity: GoogleIdentityService,
    private externalId: ExternalIdSignInService,
    private homeRealm: HomeRealmService,
    private cdr: ChangeDetectorRef
  ) {}

  // ── ONE EMAIL BOX (home-realm discovery, Joel 2026-10-07) ───────────────────────────────────
  //
  // "why do we need two buttons? that is what I'm trying to get rid of." The page asks for an
  // email, the server says which sign-in that domain uses, and the right one starts with the email
  // prefilled. There is NO choice of identity system anywhere on this page, including on failure:
  // a failed lookup is a clear error with Retry, never a fallback list of sign-in methods.

  /** The address being signed in with: the remembered one (opt-in) or what the user types. */
  email = readRememberedEmail();

  /** "Remember my email on this device": opt-in, default OFF; pre-ticked only if one is remembered. */
  rememberMe = !!this.email;

  /** enter: the form. discovering: asking the server. redirecting: leaving for the identity provider. google: Google's own button. */
  phase: 'enter' | 'discovering' | 'redirecting' | 'google' = 'enter';

  /** Why the last attempt stopped, for the error line. null = no error. */
  failure: DiscoverFailure | 'start-failed' | null = null;

  get emailValid(): boolean {
    return isPlausibleEmail(this.email);
  }

  /** Continue: remember (or forget) per the checkbox, then route by the domain. */
  onContinue(): void {
    if (!this.emailValid || this.phase === 'discovering' || this.phase === 'redirecting') return;
    const email = this.email.trim();
    rememberEmail(email, this.rememberMe);
    this.failure = null;
    this.phase = 'discovering';
    this.homeRealm
      .discover(email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (route) => this.startSignIn(route, email),
        error: (err: unknown) => {
          this.phase = 'enter';
          this.failure = err instanceof DiscoverError ? err.kind : 'unavailable';
        },
      });
  }

  /** "Not you?": forget the remembered address and start over. */
  notYou(): void {
    forgetRememberedEmail();
    this.email = '';
    this.rememberMe = false;
    this.failure = null;
    this.phase = 'enter';
  }

  private startSignIn(route: SignInRoute, email: string): void {
    if (route === 'entra') {
      this.phase = 'redirecting';
      this.authService.loginWithHint(email, domainOf(email));
      return;
    }
    if (route === 'external') {
      // An 'external' answer on a build without External ID configured is a mismatch between the
      // server and this build, not a reason to guess: say it, with Retry.
      if (!this.externalId.enabled()) {
        this.phase = 'enter';
        this.failure = 'unavailable';
        return;
      }
      this.phase = 'redirecting';
      this.externalId.signIn('/', email).catch((err: unknown) => {
        console.warn('External ID sign-in could not start', (err as { errorCode?: string })?.errorCode ?? err);
        this.phase = 'enter';
        this.failure = 'start-failed';
      });
      return;
    }
    // google: Google requires ITS rendered button (branding), so the one control shown is Google's,
    // preselecting this email. Still one way in for this address, not a choice between systems.
    if (!this.googleSignInAvailable) {
      this.phase = 'enter';
      this.failure = 'unavailable';
      return;
    }
    this.phase = 'google';
    this.cdr.detectChanges();
    if (this.googleButtonHost) {
      this.googleIdentity.renderButton(this.googleButtonHost.nativeElement, email).catch(() => {
        this.phase = 'enter';
        this.failure = 'start-failed';
      });
    }
  }

  ngAfterViewInit(): void {
    // Rendered here, not in a constructor. The deleted prototype initialised Google Identity
    // Services from its CONSTRUCTOR, which is why hiding its button did not stop it contacting
    // Google. Nothing here reaches Google unless this view actually renders the host.
    // Google's button is rendered only when discovery routes an email to Google (startSignIn); the
    // page no longer offers it up front. Its sign-in result is still handled here.
    if (!this.googleSignInAvailable) return;

    this.googleIdentity.signedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe((signedIn) => {
        if (signedIn) this.onGoogleSignedIn();
      });
  }

  /**
   * A Google ID token is now held in memory. Identity is proven; AUTHORIZATION is not, and is not
   * ours to decide — the interceptor attaches the token and DataServices resolves the company from
   * the verified `sub` and `hd`.
   *
   * So the only thing to do here is run the same authorization check the Microsoft path runs, and
   * distinguish its two failure shapes: "not onboarded" (403, no company) from any other refusal.
   */
  private onGoogleSignedIn(): void {
    this.googleNotOnboarded = false;
    this.checkAuthorizationForGoogle();
  }

  /** Counts LoginComponent instances for this page load. */
  private static initCount = 0;

  ngOnInit(): void {
    // INSTANCE, not just entry. Two of these means the component MOUNTED twice
    // (routing or bootstrap); one of these with two authorization checks means a
    // stream fired twice inside a single instance. Those need different fixes,
    // and the counts alone cannot tell them apart.
    console.log(`LOGIN-INIT #${++LoginComponent.initCount}`);
    console.log('ngOnInit Called. isAuthenticated:', this.isAuthenticated);

    // Subscribe to Authentication status
    this.authService.isAuthenticated$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isAuthenticated) => {
        this.isAuthenticated = isAuthenticated;
        console.log(
          'Authentication Status Sub > isAuthenticated:',
          this.isAuthenticated
        );
      });

    // AUTHORIZATION IS CHECKED ONCE PER ACCOUNT.
    //
    // This was a NESTED subscribe: isAuthenticated$ -> activeAccount$, with the
    // inner one created afresh on every outer emission and never torn down. MSAL
    // emits both several times while it initialises, so the effect multiplies —
    // N outer emissions x M inner ones — and every result called
    // checkAuthorization, which navigates to '/' and re-activates MainLayout.
    //
    // That is the whole multi-init: CheckUserExists and GetProjectlist three
    // times, the historical cascade twice, from ONE page load. The duplicates
    // were never in the loaders; they were app inits.
    //
    // Flattened with switchMap so exactly one inner subscription exists, and
    // gated on the account ID so re-emissions of the SAME account — which is
    // most of what MSAL emits during startup — do nothing.
    this.authService.isAuthenticated$
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        switchMap(() => this.authService.activeAccount$),
        filter((account) => !!account?.localAccountId),
        distinctUntilChanged(
          (a, b) => a?.localAccountId === b?.localAccountId
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((account) => {
        console.log('login comp > checking authorization for', account?.localAccountId);
        this.checkAuthorization(account!.tenantId, account!.localAccountId);
      });

    // Call the function to show the div after 3 seconds (3000 milliseconds)
    this.showLoginAreaAfterDelay(2000);

    //this.showLoginAreaAfterDelay(0);
  }


  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Route on the authorization check.
   *
   * THREE outcomes, and only one of them may send the user to /unauthorized.
   * The old code sent them there for any failure and cached roles=[] on the way,
   * which latched a perfectly valid user out of the app until they cleared
   * storage — a transient 500 or a token not yet acquired was indistinguishable
   * from a genuine refusal.
   */
  checkAuthorization(tenantId: string, objectId: string): void {
    this.authCheckFailed = false;
    this.projectService.checkUserAuthorization(tenantId, objectId).subscribe(
      (result) => {
        console.log('Authorization Check Response:', result);

        if (result.kind === 'authorized') {
          const user = result.user as VericureUser;
          console.log('User is authorized with roles:', user.roles);
          this.authService.setRolesFromCosmos(user);
          this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
          return;
        }

        if (result.kind === 'denied') {
          console.log('User is not authorized (server said no)');
          this.authService.setRolesFromCosmos(null);
          this._router.navigate(['/unauthorized']);
          return;
        }

        // UNAVAILABLE: we never got an answer. Retries are already exhausted by
        // this point. Say so and offer another go — but do NOT write roles, and
        // do NOT route to /unauthorized. Recording a verdict we were never given
        // is what made the failure permanent.
        console.error(
          `Authorization check UNAVAILABLE (status=${result.status}) — not a ` +
            `denial. Leaving roles untouched.`,
          result.message
        );
        this.authCheckFailed = true;
      },
      (error) => {
        // The observable is not expected to error — every path is mapped to a
        // result — so reaching here is itself a bug worth seeing rather than a
        // reason to lock someone out.
        console.error('Authorization Check Error (unmapped):', error);
        this.authCheckFailed = true;
      }
    );
  }

  /** Retry the check after an unavailable result. Bound to the retry control. */
  retryAuthorization(): void {
    this.authService.activeAccount$.pipe(take(1)).subscribe((account) => {
      if (account?.localAccountId) {
        this.checkAuthorization(account.tenantId, account.localAccountId);
      }
    });
  }

  /**
   * The authorization check for a Google session.
   *
   * BOTH PARAMETERS ARE DELIBERATELY EMPTY. CheckUserExists takes the caller's identity from the
   * TOKEN and only accepts a query objectId that MATCHES it — an absent one is fine and is the
   * safer call. Passing a client-derived id here would be the same shape as the GetCustomers IDOR:
   * an identity asserted by the caller rather than proven by the token. The server already has
   * everything it needs (verified `sub` and `hd`, resolved through IdentityProviderProfile).
   *
   * The one divergence from the Microsoft path is what 'denied' MEANS. For Google it is almost
   * always "this person is real but nobody has onboarded them yet", so it gets its own state rather
   * than /unauthorized — see googleNotOnboarded.
   */
  private checkAuthorizationForGoogle(): void {
    this.authCheckFailed = false;
    this.projectService.checkUserAuthorization('', '').subscribe(
      (result) => {
        if (result.kind === 'authorized') {
          this.authService.setRolesFromCosmos(result.user as VericureUser);
          this._router.navigate(['/'], { queryParams: { openProjects: 'true' } });
          return;
        }

        if (result.kind === 'denied') {
          this.authService.setRolesFromCosmos(null);
          this.googleNotOnboarded = true;
          return;
        }

        // UNAVAILABLE: no answer was given. Do not record a verdict we never got.
        this.authCheckFailed = true;
      },
      () => {
        this.authCheckFailed = true;
      }
    );
  }

  showLoginAreaAfterDelay(delayMilliseconds: number): void {
    if (!this.authStatusChceked) {
      setTimeout(() => {
        this.authStatusChceked = true;
      }, delayMilliseconds);
    }
  }

}
